/**
 * Guided-planning coverage assessment: the shared assessment with guided flow policy. Existing
 * policies count only when the "existing LTC coverage" type was selected; a completed session totals
 * its snapshot (self-funding + policy) instead of the live combined figure; no account is required.
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFundingAssessment } from '@/features/funding-sources';
import { fetchClientByClientIdRequest } from '@client/model';
import { selectMaybeClient } from '@client/model/selectors';
import { useSession } from '@/features/guided-financial-planning/context';
import { useFundingSources } from './use-funding-sources';

export function useGuidedCoverageAssessment() {
  const dispatch = useDispatch();
  const client = useSelector(selectMaybeClient);
  const { session } = useSession();
  const fundingSources = useFundingSources();
  const clientId = client?.clientId;

  const retryClient = useCallback(() => {
    if (clientId) dispatch(fetchClientByClientIdRequest(clientId));
  }, [dispatch, clientId]);

  return useFundingAssessment({
    client,
    clientStatus: client ? 'ready' : 'loading',
    retryClient,
    requireSelfFundingAccount: false,
    includeRecordedPolicies: (session?.stepData?.selectedAssets ?? []).includes(
      'existing-ltc-policy',
    ),
    combinedCoverageRule: session?.status === 'completed' ? 'self-plus-policy' : 'engine',
    fundingSources,
  });
}
